import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../state/AuthContext';
import {
  finishQuizAttempt,
  listAccessibleChildren,
  listAvailableQuizzes,
  loadAttemptQuestions,
  startQuizAttempt,
  submitQuizAnswer,
} from '../lib/quizRepository';
import type {
  AnswerFeedback,
  AvailableQuiz,
  QuizChild,
  QuizContentItem,
  QuizQuestion,
  QuizAttemptResult,
} from '../lib/quizRepository';

type AnswerState = string | string[] | Record<string, string>;

function displayItem(item: QuizContentItem) {
  return item.text?.trim() || item.visual?.alt?.trim() || item.id;
}

function visualIsPending(item: QuizContentItem) {
  return Boolean(item.visual && !item.visual.public_url);
}

function choiceResponse(answer: AnswerState) {
  return { option_ids: Array.isArray(answer) ? answer : [answer] };
}

function buildResponse(question: QuizQuestion, answer: AnswerState): Record<string, unknown> {
  if (question.questionType === 'sequence') return { ordered_item_ids: answer };
  if (question.questionType === 'matching') {
    const matches = answer as Record<string, string>;
    const leftItems = (question.stimulus.left_items ?? []) as QuizContentItem[];
    return { pairs: leftItems.map((item) => [item.id, matches[item.id]]) };
  }
  if (question.questionType === 'multi_select') return choiceResponse(answer);
  if (question.questionType === 'fill_blank' || question.questionType === 'short_answer') {
    return { text: answer };
  }
  return choiceResponse(answer);
}

function answerIsComplete(question: QuizQuestion, answer: AnswerState | null) {
  if (!answer) return false;
  if (question.questionType === 'matching') {
    const leftItems = (question.stimulus.left_items ?? []) as QuizContentItem[];
    const matches = answer as Record<string, string>;
    return leftItems.length > 0 && leftItems.every((item) => Boolean(matches[item.id]));
  }
  if (question.questionType === 'sequence') {
    const items = (question.stimulus.items ?? []) as QuizContentItem[];
    return Array.isArray(answer) && answer.length === items.length;
  }
  return Array.isArray(answer) ? answer.length > 0 : String(answer).trim().length > 0;
}

function ChoiceAnswer({ question, answer, onChange }: {
  question: QuizQuestion;
  answer: AnswerState | null;
  onChange(answer: AnswerState): void;
}) {
  const selected = Array.isArray(answer) ? answer : answer ? [answer as string] : [];
  const multi = question.questionType === 'multi_select';

  return (
    <div className="quiz-choice-grid" role="group" aria-label="Answer choices">
      {question.options.map((option) => {
        const active = selected.includes(option.id);
        return (
          <button
            className={`quiz-choice ${active ? 'selected' : ''}`}
            key={option.id}
            type="button"
            aria-pressed={active}
            onClick={() => {
              if (!multi) return onChange(option.id);
              onChange(active ? selected.filter((id) => id !== option.id) : [...selected, option.id]);
            }}
          >
            <span className="quiz-choice-letter">{option.id}</span>
            <span>{displayItem(option)}</span>
            {visualIsPending(option) && <small>Picture rendering pending</small>}
          </button>
        );
      })}
    </div>
  );
}

function SequenceAnswer({ question, answer, onChange }: {
  question: QuizQuestion;
  answer: AnswerState | null;
  onChange(answer: AnswerState): void;
}) {
  const items = (question.stimulus.items ?? []) as QuizContentItem[];
  const orderedIds = Array.isArray(answer) ? answer : [];
  const selectedItems = orderedIds.map((id) => items.find((item) => item.id === id)).filter(Boolean) as QuizContentItem[];

  return (
    <div className="quiz-sequence">
      <p className="quiz-instruction">Tap each card in the correct order.</p>
      <div className="sequence-result" aria-label="Your sequence">
        {selectedItems.length ? selectedItems.map((item, index) => (
          <button key={item.id} type="button" onClick={() => onChange(orderedIds.filter((id) => id !== item.id))}>
            <b>{index + 1}</b> {displayItem(item)}
          </button>
        )) : <span>Your ordered cards will appear here.</span>}
      </div>
      <div className="quiz-choice-grid">
        {items.filter((item) => !orderedIds.includes(item.id)).map((item) => (
          <button className="quiz-choice" key={item.id} type="button" onClick={() => onChange([...orderedIds, item.id])}>
            <span>{displayItem(item)}</span>
            {visualIsPending(item) && <small>Picture rendering pending</small>}
          </button>
        ))}
      </div>
    </div>
  );
}

function MatchingAnswer({ question, answer, onChange }: {
  question: QuizQuestion;
  answer: AnswerState | null;
  onChange(answer: AnswerState): void;
}) {
  const leftItems = (question.stimulus.left_items ?? []) as QuizContentItem[];
  const rightItems = (question.stimulus.right_items ?? []) as QuizContentItem[];
  const matches = answer && !Array.isArray(answer) && typeof answer === 'object' ? answer : {};

  return (
    <div className="matching-grid">
      {leftItems.map((left) => (
        <label key={left.id}>
          <span>{displayItem(left)}</span>
          <select
            value={matches[left.id] ?? ''}
            onChange={(event) => onChange({ ...matches, [left.id]: event.target.value })}
          >
            <option value="">Choose a match</option>
            {rightItems.map((right) => <option key={right.id} value={right.id}>{displayItem(right)}</option>)}
          </select>
        </label>
      ))}
    </div>
  );
}

function QuestionAnswer({ question, answer, onChange }: {
  question: QuizQuestion;
  answer: AnswerState | null;
  onChange(answer: AnswerState): void;
}) {
  if (question.questionType === 'matching') return <MatchingAnswer question={question} answer={answer} onChange={onChange} />;
  if (question.questionType === 'sequence') return <SequenceAnswer question={question} answer={answer} onChange={onChange} />;
  if (question.questionType === 'fill_blank' || question.questionType === 'short_answer') {
    return <input className="quiz-text-answer" value={(answer as string | null) ?? ''} onChange={(event) => onChange(event.target.value)} placeholder="Type your answer" />;
  }
  return <ChoiceAnswer question={question} answer={answer} onChange={onChange} />;
}

function QuizResultCard({ result, onRestart }: { result: QuizAttemptResult; onRestart(): void }) {
  const percentage = result.maxScore > 0 ? Math.round((result.score / result.maxScore) * 100) : 0;
  return (
    <section className="quiz-result panel">
      <img src="/brand/piko-parrot-mascot.png" alt="Piko parrot mascot" />
      <p className="eyebrow">Quiz complete</p>
      <h2>{percentage >= 80 ? 'Brilliant work!' : percentage >= 50 ? 'Good effort!' : 'Keep practising!'}</h2>
      <strong>{result.score} / {result.maxScore}</strong>
      <p>Your secure result has been saved to staging.</p>
      <button className="button" type="button" onClick={onRestart}>Back to quizzes</button>
    </section>
  );
}

export function StudentQuizPage() {
  const { user, isSupabaseConfigured } = useAuth();
  const [children, setChildren] = useState<QuizChild[]>([]);
  const [childId, setChildId] = useState('');
  const [quizzes, setQuizzes] = useState<AvailableQuiz[]>([]);
  const [activeQuiz, setActiveQuiz] = useState<AvailableQuiz | null>(null);
  const [attemptId, setAttemptId] = useState('');
  const [questions, setQuestions] = useState<QuizQuestion[]>([]);
  const [questionIndex, setQuestionIndex] = useState(0);
  const [answer, setAnswer] = useState<AnswerState | null>(null);
  const [feedback, setFeedback] = useState<AnswerFeedback | null>(null);
  const [result, setResult] = useState<QuizAttemptResult | null>(null);
  const [busy, setBusy] = useState(true);
  const [message, setMessage] = useState('');

  const selectedChild = useMemo(() => children.find((child) => child.id === childId) ?? null, [children, childId]);
  const currentQuestion = questions[questionIndex] ?? null;

  const loadChildren = useCallback(async () => {
    if (!isSupabaseConfigured || user?.demo) {
      setBusy(false);
      return;
    }
    setBusy(true);
    setMessage('');
    try {
      const accessible = await listAccessibleChildren();
      setChildren(accessible);
      setChildId((current) => current || accessible[0]?.id || '');
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Unable to load learner profiles.');
    } finally {
      setBusy(false);
    }
  }, [isSupabaseConfigured, user?.demo]);

  useEffect(() => { void loadChildren(); }, [loadChildren]);

  useEffect(() => {
    if (!childId || attemptId) return;
    let active = true;
    setBusy(true);
    setMessage('');
    void listAvailableQuizzes(childId)
      .then((available) => { if (active) setQuizzes(available); })
      .catch((error: unknown) => { if (active) setMessage(error instanceof Error ? error.message : 'Unable to load quizzes.'); })
      .finally(() => { if (active) setBusy(false); });
    return () => { active = false; };
  }, [childId, attemptId]);

  async function beginQuiz(quiz: AvailableQuiz) {
    setBusy(true);
    setMessage('');
    try {
      const newAttemptId = await startQuizAttempt(quiz, childId);
      const secureQuestions = await loadAttemptQuestions(newAttemptId);
      if (!secureQuestions.length) throw new Error('This quiz has no available questions.');
      setActiveQuiz(quiz);
      setAttemptId(newAttemptId);
      setQuestions(secureQuestions);
      setQuestionIndex(0);
      setAnswer(null);
      setFeedback(null);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Unable to start the quiz.');
    } finally {
      setBusy(false);
    }
  }

  async function submitCurrentAnswer() {
    if (!currentQuestion || !answerIsComplete(currentQuestion, answer)) return;
    setBusy(true);
    setMessage('');
    try {
      const secureFeedback = await submitQuizAnswer(
        attemptId,
        currentQuestion.questionVersionId,
        buildResponse(currentQuestion, answer as AnswerState),
      );
      setFeedback(secureFeedback);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Unable to submit this answer.');
    } finally {
      setBusy(false);
    }
  }

  async function nextQuestion() {
    if (questionIndex < questions.length - 1) {
      setQuestionIndex((index) => index + 1);
      setAnswer(null);
      setFeedback(null);
      setMessage('');
      return;
    }
    setBusy(true);
    try {
      setResult(await finishQuizAttempt(attemptId));
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Unable to finish the quiz.');
    } finally {
      setBusy(false);
    }
  }

  function resetQuiz() {
    setActiveQuiz(null);
    setAttemptId('');
    setQuestions([]);
    setQuestionIndex(0);
    setAnswer(null);
    setFeedback(null);
    setResult(null);
    setMessage('');
  }

  if (!isSupabaseConfigured || user?.demo) {
    return (
      <div className="portal-page">
        <section className="empty-state">
          <img src="/brand/piko-parrot-mascot.png" alt="Piko parrot mascot" />
          <h3>Secure quiz mode requires a real staging login</h3>
          <p>Demo roles cannot start database attempts or submit answers.</p>
        </section>
      </div>
    );
  }

  if (result) return <div className="portal-page"><QuizResultCard result={result} onRestart={resetQuiz} /></div>;

  return (
    <div className="portal-page quiz-page">
      <header className="page-header">
        <div>
          <p className="eyebrow">Secure student engine</p>
          <h1>{activeQuiz?.title ?? 'Choose a quiz'}</h1>
          <p>{activeQuiz ? `${selectedChild?.displayName} · answers checked privately` : 'Select a learner and an available staging quiz.'}</p>
        </div>
        {activeQuiz && <span className="pill green">No answer keys in browser</span>}
      </header>

      {message && <div className="form-message error quiz-message">{message}</div>}
      {busy && <div className="quiz-busy">Piko is working…</div>}

      {!activeQuiz && !busy && (
        <section className="quiz-picker">
          <aside className="panel">
            <label>
              Learner
              <select value={childId} onChange={(event) => setChildId(event.target.value)}>
                {children.map((child) => <option key={child.id} value={child.id}>{child.displayName} · {child.levelCode ?? 'Level not set'}</option>)}
              </select>
            </label>
            {!children.length && <p>No accessible learner profile was found. A Super Admin must create or link one first.</p>}
          </aside>
          <div className="quiz-list">
            {quizzes.map((quiz) => (
              <article className="panel quiz-list-card" key={`${quiz.quizId}-${quiz.assignmentId ?? 'open'}`}>
                <div>
                  <span className="pill purple">{quiz.subjectName ?? 'Mixed subject'}</span>
                  <h2>{quiz.title}</h2>
                  <p>{quiz.description || 'A secure QuizPiko practice quiz.'}</p>
                </div>
                <dl>
                  <div><dt>Level</dt><dd>{quiz.levelName ?? 'All'}</dd></div>
                  <div><dt>Questions</dt><dd>{quiz.questionCount}</dd></div>
                  <div><dt>Time</dt><dd>{quiz.timeLimitSeconds ? `${Math.ceil(quiz.timeLimitSeconds / 60)} min` : 'No limit'}</dd></div>
                </dl>
                <button className="button" type="button" onClick={() => void beginQuiz(quiz)}>Start quiz</button>
              </article>
            ))}
            {children.length > 0 && quizzes.length === 0 && (
              <section className="empty-state"><h3>No quiz is assigned yet</h3><p>Run the staging test-quiz seed after Migration 16.</p></section>
            )}
          </div>
        </section>
      )}

      {activeQuiz && currentQuestion && (
        <section className="quiz-stage">
          <div className="quiz-progress-row">
            <span>Question {questionIndex + 1} of {questions.length}</span>
            <progress value={questionIndex + (feedback ? 1 : 0)} max={questions.length} />
            <span>{currentQuestion.marks} mark{currentQuestion.marks === 1 ? '' : 's'}</span>
          </div>
          <article className="quiz-question-card">
            <span className="pill">{currentQuestion.questionType.replaceAll('_', ' ')}</span>
            <h2 dir={currentQuestion.language === 'ur' ? 'rtl' : 'ltr'}>{currentQuestion.prompt}</h2>
            <QuestionAnswer question={currentQuestion} answer={answer} onChange={setAnswer} />
            {feedback && (
              <div className={`answer-feedback ${feedback.isCorrect === true ? 'correct' : feedback.isCorrect === false ? 'incorrect' : 'recorded'}`}>
                <strong>{feedback.isCorrect === true ? 'Correct!' : feedback.isCorrect === false ? 'Not this time.' : 'Answer recorded securely.'}</strong>
                {feedback.explanation && <span>{feedback.explanation}</span>}
              </div>
            )}
            <div className="quiz-footer-actions">
              {!feedback
                ? <button className="button" type="button" disabled={busy || !answerIsComplete(currentQuestion, answer)} onClick={() => void submitCurrentAnswer()}>Check answer</button>
                : <button className="button" type="button" onClick={() => void nextQuestion()}>{questionIndex === questions.length - 1 ? 'Finish quiz' : 'Next question'}</button>}
            </div>
          </article>
        </section>
      )}
    </div>
  );
}
